import {
  BUSINESS_ROUTE,
  URGENT_ROUTE,
  CONTACT_US_ROUTE,
  CREATE_DEAL_ROUTE,
  EDIT_DEAL_ROUTE,
  LCW_BASE_ROUTE,
  RWC_ROUTE,
  ONBOARD_ENTERPRISE_ROUTE,
  SHAREABLE_LINKS_ROUTE,
  RFI_LINK,
  ETAFORUM_ROUTE,
  PRIVATE_LISTING_ROUTE,
  ACCOUNTANT_ROUTE,
  PREMIUM_BUYER_SUBSCRIPTION_ROUTE,
  CONFIDENTIALITY_AGREEMENT_ROUTE,
  AUTH_ROUTE,
  LOGIN_ROUTE,
  SIGNUP_ROUTE,
  RESET_PASSWORD_ROUTE,
  SOCIAL_LOGIN_ROUTE,
  FORGOT_PASSWORD_ROUTE,
  TWO_FA_ROUTE,
  DATA_ROOM_ROUTE,
  SELLING_PORTFOLIO_ROUTE,
  BUYING_PORTFOLIO_ROUTE,
  SEARCH_ROUTE,
  CONTACTS_ROUTE,
  MESSAGING_ROUTE,
  NOTIFICATIONS_ROUTE,
  ENTERPRISE_ROUTE,
  INVESTOR_VERIFICATION_ROUTE,
  PROFILE_ROUTE,
  ADMIN_DASHBOARD_ROUTE,
  ADMIN_DASHBOARD_SPONSORED_ROUTE,
  ADMIN_DASHBOARD_BROKER_LIST_ROUTE,
  ADMIN_DASHBOARD_BOSS_MODE_FORENSICS,
  ADMIN_DASHBOARD_BOSS_MODE_SUGGESTED_ACTIONS,
  ADMIN_DASHBOARD_LISTING_METRICS_ROUTE,
  ADMIN_DASHBOARD_ENTERPRISE_ROUTE,
  ADMIN_DASHBOARD_EDIT_PROFILE,
  BASE_PROFILE_ROUTE,
  DASHBOARD_DR_ROUTE,
  DOCUMENTS_DR_ROUTE,
  FAQ_DR_ROUTE,
  MEMBERS_DR_ROUTE,
  NDAS_DR_ROUTE,
  QANDA_DR_ROUTE,
  REPORTS_LOG_DR_ROUTE,
  REPORTS_OVERWIEW_DR_ROUTE,
  RFI_DR_ROUTE,
  SETTINGS_DR_ROUTE,
  FILE_SECURITY_DR_ROUTE,
  WAITING_ROOM_DR_ROUTE,
  WATCHLIST_PORTFOLIO_ROUTE,
  LISTING_INFORMATION_ROUTE,
  BUYING_PROFILE_ROUTE,
  DEAL_ROUTE,
  LEADS_ROUTE,
  SETTINGS_ENGUIRE_ROUTE,
  SETTINGS_ACCEPT_ROUTE,
  SETTINGS_BIDDER_ROUTE,
  SETTINGS_PROSPECT_ROUTE,
  QUALIFICATION_QUESTIONS,
  DOCUSIGN_URL,
  EMAIL_VERIFICATION,
  MAGIC_LINK,
  SEND_PASSWORD_FOR_SHAREABLE_LINK,
  PROFILE_PERSONAL_DETAILS_ROUTE,
  PROFILE_ACCOUNT,
  ADMIN_DASHBOARD_BOSS_MODE_ACCOUNTS,
  LCW_BUSINESS_INFO_ROUTE,
  LCW_CHECKOUT_ROUTE,
  LCW_LISTING_OPTIONS_ROUTE,
  LCW_MEDIA_PACKAGE_ROUTE,
  LCW_STATEMENT_OF_INFORMATION_ROUTE,
  EDIT_ROUTE,
  ADMIN_DASHBOARD_ACTIVITY_METRICS_ROUTE,
} from 'data/global';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const EXCLUDE_PAGES = [
  // Redirect and action pages
  SOCIAL_LOGIN_ROUTE,
  EMAIL_VERIFICATION,
  DOCUSIGN_URL,
  MAGIC_LINK,
  SEND_PASSWORD_FOR_SHAREABLE_LINK,
];

type Pages = { route: string; title: string };

const useGetPageTitle = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const checkPathname = useCallback(
    (route: string) => pathname.includes(route),
    [pathname],
  );

  const isCurrentLocation = useCallback(
    (route: string) => pathname === route,
    [pathname],
  );

  const findTitleForPage = useCallback(
    (pages: Pages[], prefixTranslation = '', fallbackTranslation = '') => {
      const pageTitle = pages.find(({ route }) => checkPathname(route))?.title;

      return pageTitle
        ? `${prefixTranslation ? `${prefixTranslation} - ` : ''}${pageTitle}`
        : fallbackTranslation;
    },
    [checkPathname],
  );

  const AUTH_PATHNAMES = useMemo(
    () => [
      { route: LOGIN_ROUTE, title: t('login') },
      { route: SIGNUP_ROUTE, title: t('signUp') },
      { route: RESET_PASSWORD_ROUTE, title: t('resetPassword') },
      { route: FORGOT_PASSWORD_ROUTE, title: t('forgotPassword') },
      { route: TWO_FA_ROUTE, title: t('twoFA') },
    ],
    [t],
  );

  const checkAuthPathname = useCallback(
    () => findTitleForPage(AUTH_PATHNAMES),
    [AUTH_PATHNAMES, findTitleForPage],
  );

  const DATA_ROOM_PATHNAMES_BASE = t('dataRoom');

  const DATA_ROOM_PATHNAMES = useMemo(
    () => [
      {
        route: DASHBOARD_DR_ROUTE,
        title: t('dashboard'),
      },
      {
        route: DOCUMENTS_DR_ROUTE,
        title: t('files'),
      },
      {
        route: QANDA_DR_ROUTE,
        title: t('q&a'),
      },
      {
        route: FAQ_DR_ROUTE,
        title: t('faqs'),
      },
      {
        route: MEMBERS_DR_ROUTE,
        title: t('dataRoomMembers'),
      },
      {
        route: WAITING_ROOM_DR_ROUTE,
        title: t('waitingRoom'),
      },
      {
        route: SHAREABLE_LINKS_ROUTE,
        title: t('secureLinks'),
      },
      {
        route: RFI_DR_ROUTE,
        title: t('fileRequests'),
      },
      {
        route: REPORTS_OVERWIEW_DR_ROUTE,
        title: `${t('analytics&Insights')} ${t('overview')}`,
      },
      {
        route: REPORTS_LOG_DR_ROUTE,
        title: `${t('analytics&Insights')} ${t('activityLog')}`,
      },
      {
        route: NDAS_DR_ROUTE,
        title: t('ndas'),
      },
      {
        route: SETTINGS_DR_ROUTE,
        title: t('setUpDataRoom'),
      },
      {
        route: FILE_SECURITY_DR_ROUTE,
        title: t('fileSecurity'),
      },
    ],
    [t],
  );

  const checkDataRoomPathname = useCallback(
    () => findTitleForPage(DATA_ROOM_PATHNAMES, DATA_ROOM_PATHNAMES_BASE),
    [DATA_ROOM_PATHNAMES, DATA_ROOM_PATHNAMES_BASE, findTitleForPage],
  );

  const SELLING_PORTFOLIO_PATHNAMES_BASE = `${t('selling')} ${t('portfolio')}`;

  const SELLING_PORTFOLIO_PATHNAMES = useMemo(
    () => [
      {
        route: DEAL_ROUTE,
        title: t('deal'),
      },
      {
        route: LEADS_ROUTE,
        title: t('leads'),
      },
      {
        route: LISTING_INFORMATION_ROUTE,
        title: t('listingInformation'),
      },
      {
        route: SETTINGS_ENGUIRE_ROUTE,
        title: t('enquiry'),
      },
      {
        route: SETTINGS_ACCEPT_ROUTE,
        title: t('accepted'),
      },
      {
        route: SETTINGS_PROSPECT_ROUTE,
        title: t('prospect'),
      },
      {
        route: SETTINGS_BIDDER_ROUTE,
        title: t('bidder'),
      },
      {
        route: QUALIFICATION_QUESTIONS,
        title: t('buyerQualifications'),
      },
    ],
    [t],
  );

  const checkSellingPortfolioPathname = useCallback(() => {
    if (isCurrentLocation(SELLING_PORTFOLIO_ROUTE))
      return SELLING_PORTFOLIO_PATHNAMES_BASE;

    return findTitleForPage(
      SELLING_PORTFOLIO_PATHNAMES,
      SELLING_PORTFOLIO_PATHNAMES_BASE,
    );
  }, [
    SELLING_PORTFOLIO_PATHNAMES,
    SELLING_PORTFOLIO_PATHNAMES_BASE,
    findTitleForPage,
    isCurrentLocation,
  ]);

  const BUYING_PORTFOLIO_PATHNAMES_BASE = `${t('buying')} ${t('portfolio')}`;

  const BUYING_PORTFOLIO_PATHNAMES = useMemo(
    () => [
      {
        route: WATCHLIST_PORTFOLIO_ROUTE,
        title: t('shortlist'),
      },
      {
        route: MESSAGING_ROUTE,
        title: t('chat'),
      },
      {
        route: LISTING_INFORMATION_ROUTE,
        title: t('deal'),
      },
      {
        route: BUYING_PROFILE_ROUTE,
        title: t('buyerProfile'),
      },
    ],
    [t],
  );

  const checkBuyingPortfolioPathname = useCallback(() => {
    if (isCurrentLocation(BUYING_PORTFOLIO_ROUTE))
      return BUYING_PORTFOLIO_PATHNAMES_BASE;

    return findTitleForPage(
      BUYING_PORTFOLIO_PATHNAMES,
      BUYING_PORTFOLIO_PATHNAMES_BASE,
    );
  }, [
    BUYING_PORTFOLIO_PATHNAMES,
    BUYING_PORTFOLIO_PATHNAMES_BASE,
    findTitleForPage,
    isCurrentLocation,
  ]);

  const ADMIN_MODE_PATHNAMES_BASE = t('bossModeDashboard');

  const ADMIN_DASHBOARD_PATHNAMES = useMemo(
    () => [
      {
        route: ADMIN_DASHBOARD_SPONSORED_ROUTE,
        title: t('sponsoredListings'),
      },
      {
        route: ADMIN_DASHBOARD_BROKER_LIST_ROUTE,
        title: t('brokerList'),
      },
      {
        route: ADMIN_DASHBOARD_BOSS_MODE_FORENSICS,
        title: `${t('bossModeOperations')} - ${t('forensics')}`,
      },
      {
        route: ADMIN_DASHBOARD_BOSS_MODE_SUGGESTED_ACTIONS,
        title: `${t('bossModeOperations')} - ${t('suggestedActionsLc')}`,
      },
      {
        route: ADMIN_DASHBOARD_LISTING_METRICS_ROUTE,
        title: t('listingMetrics'),
      },
      {
        route: ADMIN_DASHBOARD_ACTIVITY_METRICS_ROUTE,
        title: t('dealMetrics'),
      },
      {
        route: ADMIN_DASHBOARD_ENTERPRISE_ROUTE,
        title: t('manageEnterprises'),
      },
      { route: ADMIN_DASHBOARD_EDIT_PROFILE, title: t('accountSettings') },
      {
        route: ADMIN_DASHBOARD_BOSS_MODE_ACCOUNTS,
        title: t('editBossModeAccounts'),
      },
    ],
    [t],
  );

  const checkAdminDashboardPathname = useCallback(
    () =>
      findTitleForPage(
        ADMIN_DASHBOARD_PATHNAMES,
        ADMIN_MODE_PATHNAMES_BASE,
        ADMIN_MODE_PATHNAMES_BASE,
      ),
    [ADMIN_DASHBOARD_PATHNAMES, ADMIN_MODE_PATHNAMES_BASE, findTitleForPage],
  );

  const PROFILE_PATHNAMES_BASE = t('accountSettings');

  const PROFILE_PATHNAMES = useMemo(
    () => [
      {
        route: PROFILE_ROUTE,
        title: t('preferences'),
      },
      {
        route: PROFILE_PERSONAL_DETAILS_ROUTE,
        title: t('personalDetails'),
      },
      {
        route: PROFILE_ACCOUNT,
        title: t('settings'),
      },
    ],
    [t],
  );

  const checkProfilePathname = useCallback(
    () => findTitleForPage(PROFILE_PATHNAMES, PROFILE_PATHNAMES_BASE),
    [PROFILE_PATHNAMES, PROFILE_PATHNAMES_BASE, findTitleForPage],
  );

  const LCW_PATHNAMES = useMemo(
    () => [
      {
        route: LCW_BUSINESS_INFO_ROUTE,
        title: t('businessListingInfo'),
      },
      {
        route: LCW_STATEMENT_OF_INFORMATION_ROUTE,
        title: t('statementOfInformation'),
      },
      {
        route: LCW_LISTING_OPTIONS_ROUTE,
        title: t('resolvesMarketListingPackages'),
      },
      {
        route: LCW_MEDIA_PACKAGE_ROUTE,
        title: t('mediaBuyingTitle'),
      },
      {
        route: LCW_CHECKOUT_ROUTE,
        title: t('payment'),
      },
    ],
    [t],
  );

  const CREATE_LCW_PATHNAMES_BASE = t('createListing');

  const EDIT_LCW_PATHNAMES_BASE = t('editListing');

  const checkLCWRoutes = useCallback(
    () =>
      findTitleForPage(
        LCW_PATHNAMES,
        checkPathname(EDIT_ROUTE)
          ? EDIT_LCW_PATHNAMES_BASE
          : CREATE_LCW_PATHNAMES_BASE,
      ),
    [
      CREATE_LCW_PATHNAMES_BASE,
      EDIT_LCW_PATHNAMES_BASE,
      LCW_PATHNAMES,
      checkPathname,
      findTitleForPage,
    ],
  );

  const GENERAL_PATHNAMES = useMemo(
    () => [
      { route: CONTACT_US_ROUTE, title: t('contactUs') },
      { route: SEARCH_ROUTE, title: t('searchBusinesses') },
      { route: CONTACTS_ROUTE, title: t('contacts') },
      { route: MESSAGING_ROUTE, title: t('messaging') },
      { route: NOTIFICATIONS_ROUTE, title: t('notifications') },
      { route: ENTERPRISE_ROUTE, title: t('enterpriseDashboard') },
      { route: ONBOARD_ENTERPRISE_ROUTE, title: t('onboardEnterpriseMember') },
      { route: SHAREABLE_LINKS_ROUTE, title: t('secureLink') },
      { route: RFI_LINK, title: t('filevault') },
      { route: ETAFORUM_ROUTE, title: t('etaForum') },
      { route: PRIVATE_LISTING_ROUTE, title: t('privateListingLanding') },
      { route: ACCOUNTANT_ROUTE, title: t('wholesaleInvestorStatus') },
      {
        route: INVESTOR_VERIFICATION_ROUTE,
        title: t('wholesaleInvestorStatus'),
      },
      {
        route: PREMIUM_BUYER_SUBSCRIPTION_ROUTE,
        title: t('premiumBuyerService'),
      },
      {
        route: CONFIDENTIALITY_AGREEMENT_ROUTE,
        title: t('confidentialityAgreement'),
      },
      {
        route: RWC_ROUTE,
        title: t('enterpriseNameDashboard', { brandName: t('raywhite') }),
      },
      {
        route: RWC_ROUTE.toLowerCase(),
        title: t('enterpriseNameDashboard', { brandName: t('raywhite') }),
      },
    ],
    [t],
  );

  const checkGeneralPathname = useCallback(
    () => findTitleForPage(GENERAL_PATHNAMES),
    [GENERAL_PATHNAMES, findTitleForPage],
  );

  return useCallback(() => {
    if (EXCLUDE_PAGES.some(checkPathname)) return '';

    if (checkPathname(AUTH_ROUTE)) return checkAuthPathname();

    if (checkPathname(DATA_ROOM_ROUTE)) return checkDataRoomPathname();

    if (checkPathname(SELLING_PORTFOLIO_ROUTE))
      return checkSellingPortfolioPathname();

    if (checkPathname(BUYING_PORTFOLIO_ROUTE))
      return checkBuyingPortfolioPathname();

    if (checkPathname(ADMIN_DASHBOARD_ROUTE))
      return checkAdminDashboardPathname();

    if (checkPathname(BASE_PROFILE_ROUTE)) return checkProfilePathname();

    if (checkPathname(LCW_BASE_ROUTE)) return checkLCWRoutes();

    if (isCurrentLocation(BUSINESS_ROUTE)) return t('businessesForSale');

    if (isCurrentLocation(URGENT_ROUTE)) return t('urgentListings');

    if (checkPathname(CREATE_DEAL_ROUTE)) return t('createANewDeal');

    if (checkPathname(EDIT_DEAL_ROUTE)) return t('editADeal');

    // Check general routes
    const generalTitle = checkGeneralPathname();
    if (generalTitle) return generalTitle;

    return '';
  }, [
    checkPathname,
    checkAuthPathname,
    checkDataRoomPathname,
    checkSellingPortfolioPathname,
    checkBuyingPortfolioPathname,
    checkProfilePathname,
    checkAdminDashboardPathname,
    checkGeneralPathname,
    isCurrentLocation,
    checkLCWRoutes,
    t,
  ]);
};

export default useGetPageTitle;
